import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { fadeInUpVariants } from '../../hooks/useScrollAnimation';

const ExperienceSection = styled.section`
  padding: ${({ theme }) => theme.spacing.section} ${({ theme }) => theme.spacing.xl};
  max-width: ${({ theme }) => theme.maxWidth};
  margin: 0 auto;
  border-top: 1px solid ${({ theme }) => theme.colors.border};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxxl} ${({ theme }) => theme.spacing.lg};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xxxl};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-bottom: ${({ theme }) => theme.spacing.xxl};
  }
`;

const Kicker = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: 2px;
  text-transform: uppercase;
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;
const SectionTitle = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.display};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.1;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxxl};
  max-width: 1000px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.xxl};
  }
`;

const TimelineItem = styled(motion.div)`
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: ${({ theme }) => theme.spacing.xxxl};
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const ContentCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const TimelineDate = styled.span`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  display: block;
`;

const JobTitle = styled.h3`
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-family: ${({ theme }) => theme.fonts.display};
`;

const Company = styled.h4`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-weight: 500;
  font-family: ${({ theme }) => theme.fonts.body};
`;

const Description = styled.div`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const BulletPoint = styled.li`
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  list-style-type: disc;
  margin-left: 1.5em;

  &::marker {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const TechStack = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const TechTag = styled(motion.span)`
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  display: inline-block;
  cursor: default;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

interface ExperienceItem {
  position: string;
  company: string;
  period: string;
  description: string[];
  tech: string[];
}

const experienceData: ExperienceItem[] = [
  {
    position: 'Senior Mobile Engineer',
    company: 'FPT Software',
    period: '2025 — Present',
    description: [
      'Architected and maintained cross-platform mobile solutions spanning native iOS/Android and React Native frameworks, ensuring optimal performance and code reusability across platforms',
      'Led continuous maintenance and optimization of production mobile applications, implementing CI/CD pipelines, automated testing, and proactive monitoring to ensure 99.9% uptime',
      'Spearheaded AI module integration initiatives, developing intelligent features through machine learning model integration, custom training pipelines, and performance optimization for on-device inference',
    ],
    tech: ['React Native', 'iOS', 'Android', 'TypeScript', 'AI/ML', 'TensorFlow', 'CI/CD'],
  },
  {
    position: 'Frontend Developer',
    company: 'Air Asia',
    period: 'Nov 2022 — 2025',
    description: [
      'Developed and optimized high-performance ride functionality within Super App serving 30+ million users, handling complex real-time operations and user interactions',
      'Architected and implemented frontend solutions using React Native and Google Maps API to handle large-scale concurrent user activities and ride bookings',
      'Integrated and optimized payment gateway systems and ride booking functionalities while maintaining seamless user experience across iOS and Android platforms',
    ],
    tech: ['React Native', 'React', 'Google Cloud', 'Google Maps API', 'Next.js'],
  },
  {
    position: 'Middle React Native Developer',
    company: 'FPT Software',
    period: 'Feb 2022 — Nov 2022',
    description: [
      'Developed and maintained mobile application using React Native and TypeScript, implementing complex gesture animations and user interface features for worker shift management system',
      'Architected and integrated backend services using MongoDB for data persistence, Firebase for real-time features, and Azure cloud services for scalable infrastructure',
      'Led frontend development efforts including responsive UI components, custom animations, and cross-platform compatibility ensuring seamless user experience',
    ],
    tech: ['React Native', 'JavaScript', 'Redux Saga', 'GraphQL', 'AWS'],
  },
  {
    position: 'Junior React Native Developer',
    company: 'Bac Ha Software (BHSoft)',
    period: 'Jan 2020 — Jan 2022',
    description: [
      'Built front-end applications using React Native and TypeScript, focusing on mobile-first development',
      'Designed and developed REST APIs using Node.js, and implemented database schemas in PostgreSQL',
      'Built and integrated real-time features using WebSocket for live data updates and notifications',
    ],
    tech: ['React Native', 'PostgreSQL', 'TypeScript', 'Node.js', 'Firebase'],
  },
  {
    position: 'Full Stack Developer',
    company: 'Freelance',
    period: '2018 — Present',
    description: [
      'Architected and developed full-stack applications including NFC/WiFi Pass digitization system with React Native frontend and Node.js backend',
      'Designed and implemented real-time monitoring system with WebSocket integration, REST APIs, and PostgreSQL database optimization',
      'Built scalable survey platform with video processing capabilities, handling both frontend UI/UX and backend microservices',
    ],
    tech: ['React Native', 'TypeScript', 'Node.js', 'PostgreSQL', 'WebSocket'],
  },
];

const Experience: React.FC = () => {
  return (
    <ExperienceSection id="experience">
      <SectionHeader>
        <Kicker>Professional Journey</Kicker>
        <SectionTitle
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUpVariants}
        >
          Experience
        </SectionTitle>
      </SectionHeader>

      <Timeline>
        {experienceData.map((exp, index) => (
          <TimelineItem
            key={index}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={fadeInUpVariants}
            transition={{ delay: index * 0.1 }}
            whileHover={{ x: 8 }}
          >
            <TimelineDate>{exp.period}</TimelineDate>
            <ContentCol>
              <JobTitle>{exp.position}</JobTitle>
              <Company>{exp.company}</Company>
              <Description>
                <ul style={{ margin: 0, padding: 0 }}>
                  {exp.description.map((desc, i) => (
                    <BulletPoint key={i}>{desc}</BulletPoint>
                  ))}
                </ul>
              </Description>
              <TechStack>
                {exp.tech.map((tech, i) => (
                  <TechTag key={i} whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
                    {tech}
                  </TechTag>
                ))}
              </TechStack>
            </ContentCol>
          </TimelineItem>
        ))}
      </Timeline>
    </ExperienceSection>
  );
};

export default Experience;
